import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  ViewEncapsulation,
  OnInit,
  viewChild,
  ElementRef,
  signal,
  output,
  OnDestroy,
} from '@angular/core';
import { faCamera, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { ButtonComponent, DialogHeaderComponent, FaIconComponent } from '@akostm/orbit-ui';
import { DialogRef } from '@angular/cdk/dialog';
import { NgClass } from '@angular/common';
import { faTrashXmark } from '@fortawesome/pro-regular-svg-icons';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { logger } from '../../../utils';

@Component({
  selector: 'shared-take-photo-overlay',
  standalone: true,
  imports: [NgClass, ButtonComponent, DialogHeaderComponent, TranslatePipe],
  templateUrl: './take-photo-overlay.component.html',
  styleUrl: './take-photo-overlay.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TakePhotoOverlayComponent implements OnInit, OnDestroy {
  videoRef = viewChild('videoEl', { read: ElementRef });
  canvasRef = viewChild('canvasEl', { read: ElementRef });
  photoRef = viewChild('photoEl', { read: ElementRef });
  streamLoaded = signal(false);
  takenPhoto = signal<string | null>(null);
  sendPhoto = output<string | null>();
  readonly #dialogRef = inject(DialogRef);
  readonly faCamera = faCamera;
  readonly faPaperPlane = faPaperPlane;
  readonly faTrashXmark = faTrashXmark;
  readonly #translateService = inject(TranslateService);
  title = input<string>(this.#translateService.instant('SHARED.TEMPLATES.PHOTO_OVERLAY.TITLE'));
  closeLabel = input<string>(
    this.#translateService.instant('SHARED.TEMPLATES.PHOTO_OVERLAY.CLOSE')
  );

  ngOnInit(): void {
    this.startVideoStream();
  }

  ngOnDestroy(): void {
    this.stopVideoSteam();
  }

  handleSendButton(): void {
    this.sendPhoto.emit(this.takenPhoto());
  }

  closeDialog(): void {
    this.#dialogRef.close();
  }

  startVideoStream(): void {
    const videoElement = this.videoRef()?.nativeElement;

    navigator.mediaDevices
      .getUserMedia({ video: true, audio: false })
      .then((stream) => {
        videoElement.srcObject = stream;
        videoElement.play();
        this.streamLoaded.set(true);
      })
      .catch(function (err) {
        logger.error('An error occurred: ' + err);
      });
  }

  stopVideoSteam(): void {
    const videoElement = this.videoRef()?.nativeElement;
    if (!videoElement?.srcObject) return;
    const stream = videoElement.srcObject as MediaStream;
    if (!stream) return;
    const tracks = stream.getTracks();

    tracks.forEach((track) => {
      track.stop();
    });
    videoElement.srcObject = null;
    this.streamLoaded.set(false);
  }

  takePhoto() {
    const videoElement = this.videoRef()?.nativeElement;
    const canvasElement = this.canvasRef()?.nativeElement;
    const context = canvasElement.getContext('2d');

    const videoWidth = videoElement.videoWidth;
    const videoHeight = videoElement.videoHeight;
    if (videoWidth && videoHeight) {
      canvasElement.width = videoWidth;
      canvasElement.height = videoHeight;
      context.drawImage(videoElement, 0, 0, videoWidth, videoHeight);

      const data = canvasElement.toDataURL('image/png');
      this.takenPhoto.set(data);
    } else {
      this.clearPhoto();
    }
  }

  clearPhoto() {
    const canvasElement = this.canvasRef()?.nativeElement;
    const context = canvasElement.getContext('2d');
    context.fillStyle = '#AAA';
    context.fillRect(0, 0, canvasElement.width, canvasElement.height);

    this.takenPhoto.set(null);
  }
}
